/**
 * The orcaloops member surface's light / dark theme (the seam of the UI redesign).
 *
 * The theme follows the system (`prefers-color-scheme`) until a person chooses
 * one, and the choice is kept in the browser under {@link ORCALOOPS_THEME_KEY}.
 * {@link OrcaloopsFrame} writes the value as `data-theme` on `.ol-app`, which
 * is what `orcaloops.css` switches its tokens on.
 *
 * SSR/jsdom-safe: every `window` / `localStorage` / `matchMedia` access is
 * guarded, and the pure {@link computeInitialTheme} takes the window as an
 * argument so the decision is unit-tested without a browser. Mirrors
 * `clawinstall-theme.ts`, whose {@link ThemeWindowLike} it reuses.
 *
 * No `useState`, no `useEffect`.
 *
 * @since 1.0.0
 */
import { Atom } from "@effect-atom/atom-react";

import type { ThemeWindowLike } from "./clawinstall-theme.ts";

/** @since 1.0.0 */
export type OrcaloopsTheme = "light" | "dark";

/** The browser-storage key a chosen theme is kept under. @since 1.0.0 */
export const ORCALOOPS_THEME_KEY = "orcaloops-theme";

/**
 * The theme to start in: the stored choice if there is one, else the OS
 * preference, else light. `null` (no window: the server) is light.
 *
 * @since 1.0.0
 */
export const computeInitialTheme = (win: ThemeWindowLike | null): OrcaloopsTheme => {
  if (win === null) return "light";
  const stored = win.localStorage?.getItem(ORCALOOPS_THEME_KEY);
  if (stored === "light" || stored === "dark") return stored;
  return win.matchMedia?.("(prefers-color-scheme: dark)")?.matches === true ? "dark" : "light";
};

/**
 * {@link computeInitialTheme} over the real window, or light on the server.
 * Read after mount, never at import.
 *
 * @since 1.0.0
 */
export const getInitialOrcaloopsTheme = (): OrcaloopsTheme => {
  if (typeof window === "undefined") return computeInitialTheme(null);
  return computeInitialTheme(window as ThemeWindowLike);
};

/** Keep a chosen theme so the next visit starts in it. A no-op on the server. @since 1.0.0 */
export const persistOrcaloopsTheme = (theme: OrcaloopsTheme): void => {
  if (typeof window === "undefined") return;
  (window as ThemeWindowLike).localStorage?.setItem(ORCALOOPS_THEME_KEY, theme);
};

/** The opposite theme — the target of a toggle. @since 1.0.0 */
export const nextOrcaloopsTheme = (theme: OrcaloopsTheme): OrcaloopsTheme => theme === "dark" ? "light" : "dark";

/**
 * The live theme. Kept alive so a toggle survives every route change on the
 * member surface. It starts light on both sides, so the client's first frame
 * matches the server's; {@link OrcaloopsFrame} applies the stored choice
 * ({@link getInitialOrcaloopsTheme}) after mount.
 *
 * @since 1.0.0
 */
export const orcaloopsThemeAtom: Atom.Writable<OrcaloopsTheme> = Atom.make<OrcaloopsTheme>("light").pipe(
  Atom.keepAlive,
);
